/**
 * useHandoffFamilyEntry — is the family-facing in-thread handoff offered? (#1801)
 *
 * TRF's team is small. Nobody watches an inbox to join a family's thread live,
 * so every family-facing entry point to the in-thread handoff — the "→ Loop in
 * my case manager" pill, the implicit nudge cards, the "brief my case manager"
 * chip — advertises availability the org cannot keep. `CLAUDE.md`'s one hard
 * rule for this prototype is honesty about what is real, so all of them hide
 * behind one server-read flag until TRF confirms the live loop-in.
 *
 * NOTHING is deleted. The request route, the `HandoffRequest` model, the TRFer
 * inbox and the claim machinery all stay; flipping
 * `HANDOFF_FAMILY_ENTRY_ENABLED` on the server brings the feature back whole.
 *
 * The flag rides on `GET /api/handoffs/family-pending`, which ChatPage already
 * polls — the same "flag on an existing per-request payload" pattern as
 * `versioningEnabled` (chat history). This hook reuses that query's key, so a
 * second caller costs no extra request.
 *
 * DEFAULT OFF, deliberately. Before the query resolves, against an older
 * backend, or on an outright error, `enabled` is false — the honest answer when
 * we do not know is "we are not promising you a person".
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';

/** Shared with ChatPage's own family-pending query so the two dedupe. */
export const FAMILY_PENDING_QUERY_KEY = ['handoffs', 'family-pending'] as const;

/**
 * `active: false` skips the request — the route is parent-only, so a caller
 * that renders for every role (Settings) passes `isParent` and gets `false`
 * for everyone else without a 403 every 30 seconds.
 */
export function useHandoffFamilyEntry({ active = true }: { active?: boolean } = {}): { enabled: boolean } {
  const { data } = useQuery({
    queryKey: FAMILY_PENDING_QUERY_KEY,
    queryFn: () => api.handoffs.familyPending(),
    refetchInterval: 30_000,
    enabled: active,
  });

  return { enabled: data?.family_entry_enabled === true };
}
